/**
 * ADR-0214 Part 2 — the Subjects screen (cut 1: People, Mine, the subject
 * detail, the person page; admin-only until the viewer filters land).
 *
 * Types for the hub's `/api/tenant/subjects` responses, and the one place
 * for the screen's labels and formats: ≈ tokens are characters ÷ 4; every
 * time is absolute, in the workspace's zone; `person_key` is a link key only
 * and is never rendered.
 */

import { formatLastCall } from "./mcpUsage.js";

export type SubjectStatus = "active" | "inactive" | "closed";
export type SubjectsView = "people" | "mine";
export type PeopleBy = "person" | "subject";

export interface SubjectRow {
  person?: string;
  person_key?: string;
  subject_number: number | null;
  subject: string | null;
  redacted: boolean;
  opened_at: string | null;
  status: SubjectStatus | null;
  calls: number;
  chars: number;
  failed: number;
  knowledge_served: number | null;
  filed: { knowledge: number; finding: number; study: number } | null;
  asked_before: null;
}

export interface PersonRow {
  person: string;
  person_key: string;
  last_active: string | null;
  where: Array<"door" | "console">;
  subjects: number;
  novel: number;
  without_subject: number;
  pages_viewed: number;
  failed: number;
  blocked: number;
  concerns: number;
  chars: number;
  ai_tokens: number;
  active: boolean;
}

export interface SubjectsResponse {
  time_zone: string;
  views: SubjectsView[];
  rows?: SubjectRow[];
  truncated?: boolean;
  people?: PersonRow[];
}

export type ActivityOutcome = "ok" | "failed" | "blocked" | "held" | "refused";

export interface ActivityRow {
  at: string;
  where: string;
  action: string;
  outcome: ActivityOutcome;
  touched: Array<{ kind: string; name: string; access: "read" | "write" }>;
  touched_unnamed: number;
}

export interface SubjectDetailResponse {
  time_zone: string;
  subject: {
    number: number;
    subject: string;
    goal: string | null;
    person: string;
    person_key: string | null;
    redacted: boolean;
    opened_at: string;
    status: SubjectStatus;
    closed_at: string | null;
  };
  activity: ActivityRow[];
  activity_truncated: boolean;
  /** ADR-0214 Amendment 5 (F4): each time the subject was widened — the words said then; the title keeps the opening words. */
  widened: Array<{ at: string; subject: string; goal: string | null }>;
  /** ADR-0214 Amendment 5 (F1): each time a call on an object last worked under another subject moved the label here. */
  picked_up: Array<{ at: string; from: { number: number; subject: string } | null; object: string | null }>;
  knowledge_served: Array<{ name: string; how: "at_start" | "when_changed" | "before_query" }>;
  studies_served: Array<{ title: string; slug: string }>;
  studies_unnamed: number;
  /** Entries served that the viewer does not see now — counted, never named. */
  knowledge_unnamed: number;
  searches: Array<{ at: string; where: string; words: string; results: number }>;
  failed: Array<{ at: string; names: string[]; later_succeeded: boolean }>;
  not_found: Array<{ name: string; times: number }>;
  filed: Array<{ at: string; kind: "knowledge" | "finding" | "study"; name: string | null; outcome: string | null }>;
  gaps: Array<{ at: string; topic: string; words: string }>;
  concerns: Array<{ at: string; topic: string; sentiment: string | null; summary: string }>;
  answer_reports: Array<{ at: string; topic: string; verdict: string | null; words: string }>;
  usage: Array<{ category: string; calls: number; chars: number; did_not_go_through: number }>;
  asked_before: null;
}

export interface PersonPageResponse {
  time_zone: string;
  person: Omit<PersonRow, "person_key">;
  rows: SubjectRow[];
  truncated: boolean;
  without_subject: ActivityRow[];
  without_subject_truncated: boolean;
}

/** Absolute time in the workspace's zone; "—" when absent. Never relative. */
export function when(iso: string | null | undefined, timeZone: string): string {
  return formatLastCall(iso, timeZone);
}

/** Characters exchanged ÷ 4, the only ratio the screen shows: "≈ 12k", "≈ 850", "—" at 0. */
export function approxTokens(chars: number): string {
  const t = Math.round(chars / 4);
  if (t <= 0) return "—";
  if (t >= 1_000_000) return `≈ ${(t / 1_000_000).toFixed(1)}M`;
  if (t >= 1_000) return `≈ ${Math.round(t / 1_000)}k`;
  return `≈ ${t}`;
}

/** The console AI's real tokens (labelled "tokens", never "≈"). */
export function tokens(n: number): string {
  return n > 0 ? n.toLocaleString() : "—";
}

export const APPROX_TOKENS_TITLE = "Approximate: characters exchanged ÷ 4.";

export function countOrDash(n: number | null | undefined): string {
  return n ? n.toLocaleString() : "—";
}

export const STATUS_LABEL: Readonly<Record<SubjectStatus, string>> = { active: "Active", inactive: "Inactive", closed: "Closed" };

export const OUTCOME_LABEL: Readonly<Record<ActivityOutcome, string>> = {
  ok: "OK",
  failed: "Failed",
  blocked: "Blocked",
  held: "Held",
  refused: "Refused",
};

export const KNOWLEDGE_HOW_LABEL = { at_start: "At the start", when_changed: "When the subject changed", before_query: "Before a query" } as const;

export const USAGE_LABEL: Readonly<Record<string, string>> = {
  orienting: "Finding things",
  querying: "Querying",
  building: "Building",
  recording: "Recording",
  other: "Other",
};

export const SEARCH_WHERE_LABEL: Readonly<Record<string, string>> = { knowledge: "Knowledge", studies: "Studies", guidance: "Guidance" };

/** "Copilot", "Console", or both — the planes a person used in the period. */
export function whereLabel(where: Array<"door" | "console">): string {
  if (where.length === 0) return "—";
  return where.map((w) => (w === "door" ? "Assistant" : "Console")).join(", ");
}

/** "2 knowledge, 1 finding" — or "—". */
export function filedLabel(filed: SubjectRow["filed"]): string {
  if (!filed) return "—";
  const parts = [
    filed.knowledge ? `${filed.knowledge} knowledge` : null,
    filed.finding ? `${filed.finding} finding${filed.finding === 1 ? "" : "s"}` : null,
    filed.study ? `${filed.study} stud${filed.study === 1 ? "y" : "ies"}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

/** "page: Production report (wrote)". */
export function touchedLabel(t: ActivityRow["touched"][number]): string {
  return `${t.kind.replace(/_/g, " ")}: ${t.name}${t.access === "write" ? " (changed)" : ""}`;
}

export function unnamedLabel(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? "1 thing that no longer exists" : `${n} things that no longer exist`;
}

export function filedWhat(f: SubjectDetailResponse["filed"][number]): string {
  if (f.kind === "knowledge") return f.name ? `Knowledge: ${f.name}` : "Knowledge proposal";
  if (f.kind === "study") return f.name ? `Study: ${f.name}` : "A study";
  return f.name ?? "A finding";
}

export function outcomeWord(o: string | null): string {
  return o ? o.charAt(0).toUpperCase() + o.slice(1) : "—";
}
